import { AlertCircle } from "lucide-react";
import React, { useContext, useState, useMemo, useEffect } from "react";
import {
  Drawer,
  Button,
  TextInput,
  Textarea,
  MultiSelect,
  Switch,
  Alert,
  Loader,
} from "@mantine/core";
import { AuthContext } from "../../../../providers/AuthProvider";
import { SegmentationBlocksContext } from "../../providers/SegmentationBlocksProvider";
import useSegmentListApiService from "../../../segments/services/api/SegmentListApiService";
import useSegmentAssignmentPreviewApiService from "../../services/api/SegmentAssignmentPreviewApiService";

interface CreateSegmentationBlockDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const CreateSegmentationBlockDrawer: React.FC<
  CreateSegmentationBlockDrawerProps
> = ({ isOpen, setIsOpen }) => {
  const auth = useContext(AuthContext);
  const context = useContext(SegmentationBlocksContext);
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(false);

  // Fetch all segments for selection (segments can be reused across multiple
  // blocks). We intentionally do NOT request include_match_count here: that
  // computation scans the whole employee table once per segment and times out
  // for large employers, leaving the dropdown empty. The per-segment match
  // count is cosmetic for this picker; the accurate, deduplicated count is
  // fetched separately via the preview endpoint below.
  const {
    data: segmentsData,
    isLoading: isLoadingSegments,
    isError: isSegmentsError,
  } = useSegmentListApiService(
    {
      employer: auth?.employer?.id ?? 0,
    },
    !!auth?.employer?.id
  );

  const availableSegments = useMemo(() => {
    if (!segmentsData?.results) {
      return [];
    }

    return segmentsData.results.map((segment) => ({
      value: segment.id.toString(),
      label: segment.name,
    }));
  }, [segmentsData]);

  // Preview API to get deduplicated employee count
  const {
    mutate: fetchPreview,
    data: previewData,
    isLoading: isLoadingPreview,
  } = useSegmentAssignmentPreviewApiService();

  // Fetch deduplicated count when segments change
  // Note: fetchPreview is intentionally omitted from deps as it's unstable (recreated each render)
  useEffect(() => {
    if (selectedSegmentIds.length > 0) {
      const segmentIds = selectedSegmentIds.map((id) => parseInt(id, 10));
      fetchPreview({ segment_ids: segmentIds });
    }
  }, [selectedSegmentIds]); // eslint-disable-line

  const estimatedMatch = previewData?.total_matched_employee_count ?? 0;

  const resetForm = (): void => {
    setTitle("");
    setReason("");
    setSelectedSegmentIds([]);
    setIsActive(false);
  };

  const handleCreate = (): void => {
    if (
      selectedSegmentIds.length > 0 &&
      context &&
      title.trim() &&
      reason.trim()
    ) {
      // Create block with all selected segments
      const segmentIds = selectedSegmentIds.map((id) => parseInt(id, 10));
      context.createBlock(segmentIds, title, reason, isActive);
      resetForm();
      setIsOpen(false);
    }
  };

  const handleClose = (): void => {
    resetForm();
    setIsOpen(false);
  };

  const isFormValid =
    title.trim() !== "" &&
    reason.trim() !== "" &&
    selectedSegmentIds.length > 0;

  return (
    <Drawer
      opened={isOpen}
      onClose={handleClose}
      title={null}
      padding={0}
      size="xl"
      position="right"
      styles={{
        header: { display: "none" },
        drawer: { padding: 0 },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-8"
          style={{ backgroundColor: "rgba(6, 33, 51, 0.05)" }}
        >
          <h2 className="text-[18px] font-bold text-[#062133] m-0">
            New Segmentation Block
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-[#062133] hover:opacity-70 bg-transparent border-none cursor-pointer p-0"
            aria-label="Close"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              role="img"
              aria-labelledby="create-segmentation-close-title"
            >
              <title id="create-segmentation-close-title">Close</title>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-6 p-6">
          {/* Title Input */}
          <TextInput
            label={
              <span className="font-medium text-[14px] text-[#062133]">
                Title <span className="text-[#ED7687]">*</span>
              </span>
            }
            placeholder="Enter title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            styles={{
              input: {
                border: "1px solid #cdd3d6",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#384d5c",
              },
            }}
          />

          {/* Reason for block */}
          <Textarea
            label={
              <span className="font-medium text-[14px] text-[#062133]">
                Reason for block <span className="text-[#ED7687]">*</span>
              </span>
            }
            description="*Note: This message will be visible to the employee."
            placeholder="Enter reason for block"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            styles={{
              input: {
                border: "1px solid #cdd3d6",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#384d5c",
              },
            }}
          />

          {/* Select employee segments */}
          <div>
            <div className="font-medium text-[14px] text-[#062133] mb-2">
              Select employee segments
            </div>
            <p className="text-[12px] text-[#839099] mb-2 leading-4">
              Choose which employee segments to block from EWA advances
            </p>
            <MultiSelect
              placeholder={
                isSegmentsError ? "Couldn't load segments" : "Select segments"
              }
              data={availableSegments}
              value={selectedSegmentIds}
              onChange={setSelectedSegmentIds}
              disabled={isLoadingSegments || isSegmentsError}
              error={
                isSegmentsError
                  ? "Couldn't load segments. Please refresh and try again."
                  : undefined
              }
              nothingFound="No segments found"
              searchable
              styles={{
                input: {
                  border: "1px solid #cdd3d6",
                  borderRadius: "4px",
                  minHeight: "36px",
                },
                value: {
                  backgroundColor: "#384d5c",
                  color: "white",
                  fontWeight: 400,
                  fontSize: "12px",
                },
              }}
            />
          </div>

          {/* Estimated match alert */}
          {selectedSegmentIds.length > 0 && (
            <Alert
              icon={
                isLoadingPreview ? (
                  <Loader size={16} color="orange" />
                ) : (
                  <AlertCircle size={16} />
                )
              }
              color="orange"
              styles={{
                root: {
                  backgroundColor: "#FFF4EA",
                  borderRadius: "8px",
                  border: "none",
                },
                message: {
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#FF922B",
                },
                icon: {
                  color: "#FF922B",
                },
              }}
            >
              {isLoadingPreview
                ? "Calculating..."
                : `Estimated match: ${estimatedMatch} Employees`}
            </Alert>
          )}

          {/* Segment status toggle */}
          <div className="flex items-center justify-between gap-6">
            <div className="flex-1">
              <div className="font-medium text-[14px] text-[#062133] mb-2">
                Segment status
              </div>
              <p className="text-[12px] text-[#839099] leading-4">
                Activate segment by switching the toggle. Default is
                deactivated.
              </p>
            </div>
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.currentTarget.checked)}
              size="md"
              color="dark"
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 mt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              fullWidth
              styles={{
                root: {
                  border: "1px solid #062133",
                  color: "#062133",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  height: "42px",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!isFormValid}
              loading={context?.isCreating ?? false}
              fullWidth
              styles={{
                root: {
                  backgroundColor: "#062133",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  height: "42px",
                  "&:hover": {
                    backgroundColor: "#0a3d52",
                  },
                  "&:disabled": {
                    backgroundColor: "#cdd3d6",
                  },
                },
              }}
            >
              Save Segmentation Block
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default CreateSegmentationBlockDrawer;
